import { getPredefinedLeadListByKey } from "@/constants/lead-lists.constants";
import { PriorityRecordCount } from "@/queries/lead.queries";
import { LeadListFilter } from "@/types/lead-list-filter.types";

/** Green / yellow / red for priority report cells (good / warning / bad). */
export const REPORT_COLORS = {
    green: "#06D6A0", // Mint Green – good range
    yellow: "#FFBE0B", // Vivid Yellow – warning range
    red: "#FF6B6B", // Coral Red – bad range
} as const;

/** Max count (inclusive) for green and yellow per priority; above yellow max = red. */
export const PRIORITY_THRESHOLDS: Record<number, { greenMax: number; yellowMax: number }> = {
    1: { greenMax: 9, yellowMax: 29 },
    2: { greenMax: 9, yellowMax: 29 },
    3: { greenMax: 19, yellowMax: 49 },
    4: { greenMax: 99, yellowMax: 199 },
    5: { greenMax: 99, yellowMax: 199 },
    6: { greenMax: 99, yellowMax: 199 },
};

/** Score 500–599. CRM does not assign higher, so this row is shown as Extra. */
export const EXTRA_PRIORITY = 6;

/** The report always includes priorities 1–5 and Extra. */
export const getPriorityReportMaxLevel = (): number => EXTRA_PRIORITY;

export type PriorityDepartment = {
    /** Internal key used as column identifier and route param (e.g. "dach", "uk"). */
    key: string;
    /** Language code sent by the API (e.g. "DE", "ENG"). */
    apiCode: string;
    /** Human-readable column header (e.g. "DE", "UK"). */
    label: string;
};

/**
 * Active departments for Setting capacities (priority report).
 * `key` is sent as filter.key to `/lead/prio-record-counts` and echoed back as `language`.
 */
export const PRIORITY_DEPARTMENTS: PriorityDepartment[] = [
    { key: "dach", apiCode: "DE", label: "DACH" },
    { key: "prp", apiCode: "PRP", label: "PRP" },
    { key: "fr", apiCode: "FR", label: "FR" },
    { key: "es", apiCode: "ES", label: "ES" },
    { key: "uk", apiCode: "ENG", label: "UK" },
];

export const PRIORITY_DEPARTMENT_KEYS = PRIORITY_DEPARTMENTS.map((dept) => dept.key);

export function getPriorityDepartmentByKey(key: string): PriorityDepartment | undefined {
    return PRIORITY_DEPARTMENTS.find((dept) => dept.key === key);
}

export type PriorityReportCountFilter = LeadListFilter & { key: string };

/** Build count API filters from selected departments using setting-list definitions. */
export const buildPriorityReportCountFilters = (departments: PriorityDepartment[]): PriorityReportCountFilter[] =>
    departments.flatMap((dept) => {
        const list = getPredefinedLeadListByKey(dept.key);
        if (!list) {
            return [];
        }

        return [{ key: dept.key, ...list.filter }];
    });

export type PriorityTableRow = {
    priority: number;
    counts: Record<string, number>;
    /** Priority 6, labeled Extra. */
    isExtra?: boolean;
};

/** Returns the background color for a cell based on priority and count. */
export const getCellColor = (priority: number, count: number): string => {
    const thresholds = PRIORITY_THRESHOLDS[priority];
    if (!thresholds) return REPORT_COLORS.yellow;
    if (count <= thresholds.greenMax) return REPORT_COLORS.green;
    if (count <= thresholds.yellowMax) return REPORT_COLORS.yellow;
    return REPORT_COLORS.red;
};

/**
 * Transforms priority record counts into a table-ready row array.
 * `language` in the API response is the filter key (department key).
 * Rows always include priorities 1–5 and Extra.
 * Priority 6 is labeled Extra.
 */
export const buildPriorityTableData = (
    priorityRecordCounts: PriorityRecordCount[],
    departments: PriorityDepartment[] = PRIORITY_DEPARTMENTS,
    maxPriority: number = getPriorityReportMaxLevel()
): PriorityTableRow[] => {
    const dataMap = new Map<string, number>();

    priorityRecordCounts.forEach(({ priority, language, count }) => {
        if (priority > maxPriority) {
            return;
        }

        dataMap.set(`${priority}-${language}`, count);
    });

    const levels = Array.from({ length: maxPriority }, (_, index) => index + 1);

    return levels.map((priority) => ({
        priority,
        isExtra: priority === EXTRA_PRIORITY,
        counts: Object.fromEntries(departments.map((dept) => [dept.key, dataMap.get(`${priority}-${dept.key}`) ?? 0])),
    }));
};

/** Computes per-department totals across all priorities. */
export const buildTotalsByDepartment = (tableData: PriorityTableRow[]): Record<string, number> => {
    const totals: Record<string, number> = {};
    tableData.forEach((row) => {
        Object.entries(row.counts).forEach(([key, count]) => {
            totals[key] = (totals[key] ?? 0) + count;
        });
    });
    return totals;
};
